import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, ShieldOff, RefreshCw, AlertCircle,
  Clock, Shield,
} from "lucide-react";
import { apiGet, apiPost } from "../../utils/api";
import { toast } from "../../utils/toast";
import { PageHeader, FilterBar, PillSelect, DataGrid } from "../common/ui.jsx";
import "./tenant-license.css";

/* ── helpers ── */
function fmt(v) {
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return "—";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(t));
}

function deviceIcon(filter) {
  if (filter === "blocked")   return ShieldOff;
  if (filter === "requested") return Clock;
  return Shield;
}

/* ── Seat bar ── */
function SeatBar({ used, limit, pending, blocked }) {
  const pct   = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color  = pct >= 90 ? "#e74c3c" : pct >= 70 ? "#f39c12" : "#2ecc71";
  const avail  = limit === null ? "Unlimited" : Math.max(0, limit - used);

  return (
    <div className="tlm-seat-bar">
      <div className="tlm-seat-stats">
        <div className="tlm-stat">
          <span className="tlm-stat-value licensed">{used}</span>
          <span className="tlm-stat-label">Licensed</span>
        </div>
        <div className="tlm-stat">
          <span className="tlm-stat-value pending">{pending}</span>
          <span className="tlm-stat-label">Requested</span>
        </div>
        <div className="tlm-stat">
          <span className="tlm-stat-value blocked">{blocked}</span>
          <span className="tlm-stat-label">Blocked</span>
        </div>
        {limit !== null && (
          <div className="tlm-stat">
            <span className="tlm-stat-value available">{avail}</span>
            <span className="tlm-stat-label">Available</span>
          </div>
        )}
      </div>

      {limit !== null && (
        <div className="tlm-seat-progress">
          <div className="tlm-seat-progress-label">
            {used} of {limit} seats used
          </div>
          <div className="tlm-progress-track">
            <div
              className="tlm-progress-fill"
              style={{ width: `${pct}%`, background: color }}
            />
          </div>
          {pct >= 90 && (
            <div className="tlm-seat-warning">
              <AlertCircle size={12} />
              Seat limit nearly full.
            </div>
          )}
        </div>
      )}

      {limit === null && (
        <div className="tlm-seat-progress">
          <div className="tlm-seat-progress-label">Unlimited plan · {used} devices licensed</div>
        </div>
      )}
    </div>
  );
}

/* ── Device grid columns — same row layout as the Devices page,
   instead of a card grid, per explicit request. ── */
function buildColumns(filter, { onApprove, onBlock, onUnblock }) {
  const columns = [
    {
      key: "name",
      label: "Device Name",
      sortable: true,
      sortFn: (a, b) => (a.hostname || a.name || "").localeCompare(b.hostname || b.name || ""),
      render: (d) => (
        <div>
          <div style={{ fontWeight: 600 }}>{d.hostname || d.name || "Unnamed Device"}</div>
          <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>
            {d.agentId || "—"}{d.ip ? ` · ${d.ip}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "lastSeen",
      label: "Last Seen",
      sortable: true,
      sortFn: (a, b) => new Date(a.lastSeen || 0).getTime() - new Date(b.lastSeen || 0).getTime(),
      render: (d) => <span className="mono">{fmt(d.lastSeen)}</span>,
    },
  ];

  if (filter === "blocked") {
    columns.push({
      key: "blockedAt",
      label: "Blocked On",
      sortable: true,
      sortFn: (a, b) => new Date(a.blockedAt || 0).getTime() - new Date(b.blockedAt || 0).getTime(),
      render: (d) => <span className="mono" style={{ color: "var(--color-danger)" }}>{fmt(d.blockedAt)}</span>,
    });
  }

  columns.push({
    key: "actions",
    label: "",
    render: (d) => (
      <div className="gc-row-actions">
        {filter === "requested" && (
          <button
            className="gc-btn gc-btn-ghost gc-row-btn gc-row-btn-success"
            onClick={(e) => { e.stopPropagation(); onApprove(d); }}
            title="Approve — assign a seat to this device"
          >
            <CheckCircle2 size={13} /> Approve
          </button>
        )}
        {(filter === "licensed" || filter === "requested") && (
          <button
            className="gc-btn gc-btn-ghost gc-row-btn gc-row-btn-danger"
            onClick={(e) => { e.stopPropagation(); onBlock(d); }}
            title="Block — device uninstalls itself, seat freed, data preserved"
          >
            <ShieldOff size={13} /> Block
          </button>
        )}
        {filter === "blocked" && (
          <button
            className="gc-btn gc-btn-ghost gc-row-btn gc-row-btn-success"
            onClick={(e) => { e.stopPropagation(); onUnblock(d); }}
            title="Unblock — allow this device to reconnect"
          >
            <CheckCircle2 size={13} /> Unblock
          </button>
        )}
      </div>
    ),
  });

  return columns;
}

/* ── Main component ── */
export default function TenantLicenseManager() {
  const [devices,  setDevices]  = useState([]);
  const [blocked,  setBlocked]  = useState([]);
  const [summary,  setSummary]  = useState({ used: 0, limit: null, available: null, pending: 0, blocked: 0 });
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("licensed");
  const [search,   setSearch]   = useState("");
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, bRes, sRes] = await Promise.all([
        apiGet("/api/devices/manage/devices"),
        apiGet("/api/devices/manage/blocked"),
        apiGet("/api/devices/manage/summary"),
      ]);
      const [d, b, s] = await Promise.all([
        dRes.json().catch(() => []),
        bRes.json().catch(() => []),
        sRes.json().catch(() => ({})),
      ]);
      setDevices(Array.isArray(d) ? d : []);
      setBlocked(Array.isArray(b) ? b : []);
      if (s && typeof s === "object" && !s.message) setSummary(s);
    } catch {
      toast.error("Failed to load device data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const approve = async (device) => {
    const res  = await apiPost(`/api/devices/manage/devices/${device._id}/approve`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data?.message || "Approval failed"); return; }
    toast.success(data?.message || "License approved");
    void refresh();
  };

  const block = async (device) => {
    if (!window.confirm(
      `Block "${device.hostname || device.agentId}"?\n\n` +
      `• The device will receive an uninstall command\n` +
      `• Its seat will be freed immediately\n` +
      `• All data is preserved in the Blocked list\n` +
      `• You can unblock it at any time`
    )) return;
    const res  = await apiPost(`/api/devices/manage/devices/${device._id}/block`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data?.message || "Block failed"); return; }
    toast.success(data?.message || "Device blocked");
    void refresh();
  };

  const unblock = async (device) => {
    if (!window.confirm(
      `Unblock "${device.hostname || device.agentId}"?\n\n` +
      `The device will be allowed to reconnect. A new license approval will be required.`
    )) return;
    const res  = await apiPost(`/api/devices/manage/devices/${device._id}/unblock`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data?.message || "Unblock failed"); return; }
    toast.success(data?.message || "Device unblocked");
    void refresh();
  };

  /* ── Filtered list ── */
  const sourceList =
    filter === "blocked"   ? blocked :
    filter === "requested" ? devices.filter(d => !d.isLicensed) :
    devices.filter(d => d.isLicensed);

  const filtered = sourceList.filter((d) => {
    const q = search.toLowerCase();
    return !q || [d.agentId, d.hostname, d.name, d.ip].some(v => (v || "").toLowerCase().includes(q));
  });

  const filterLabel =
    filter === "blocked"   ? "Blocked"   :
    filter === "requested" ? "Requested" : "Licensed";

  /* ── Empty state icon + message ── */
  const EmptyIcon = deviceIcon(filter);
  const emptyMsg =
    filter === "blocked"   ? "No blocked devices." :
    filter === "requested" ? "No pending approval requests." :
    "No licensed devices yet.";

  return (
    <div className="tlm-wrapper">

      <PageHeader
        title="License Manager"
        subtitle="Approve requests, block devices, and monitor seat usage."
        actions={
          <>
            <button className="gc-icon-btn" onClick={refresh} disabled={loading} data-tip="Refresh">
              <RefreshCw size={14} className={loading ? "tlm-spin" : ""} />
            </button>
            <button className="gc-btn gc-btn-ghost" onClick={() => navigate("/billing")}>
              Plans &amp; Billing
            </button>
          </>
        }
      />

      {/* ── Seat bar ── */}
      <SeatBar {...summary} />

      <div className="gc-card">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name, hostname, IP…"
        >
          <PillSelect
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setSearch(""); }}
          >
            <option value="licensed">Licensed ({summary.used ?? 0})</option>
            <option value="requested">Requested ({summary.pending ?? 0})</option>
            <option value="blocked">Blocked ({summary.blocked ?? 0})</option>
          </PillSelect>

          <span className="tlm-count">
            {filtered.length} {filtered.length === 1 ? "device" : "devices"} · {filterLabel}
          </span>
        </FilterBar>

        {/* ── Device grid — same row layout as the Devices page ── */}
        {loading ? (
          <div className="tlm-empty">
            <RefreshCw size={28} className="tlm-spin" style={{ opacity: 0.35 }} />
          </div>
        ) : (
          <DataGrid
            columns={buildColumns(filter, { onApprove: approve, onBlock: block, onUnblock: unblock })}
            rows={filtered}
            getRowId={(d) => d._id}
            emptyState={
              <>
                <EmptyIcon size={40} style={{ opacity: 0.18 }} />
                <p>{search ? `No ${filterLabel.toLowerCase()} devices match the search.` : emptyMsg}</p>
              </>
            }
          />
        )}
      </div>

    </div>
  );
}
